import type { WrapperFC } from '@george-labs.com/design-system';
// eslint-disable-next-line no-restricted-imports
import { Amount, Heading } from '@george-labs.com/design-system';
import type { AmountStatics } from '@george-labs.com/design-system/types/components/Amount';
import type { HeadingProps } from '@george-labs.com/design-system/types/components/Typography/Heading';
import type { ComponentPropsWithRef, ElementType, FC } from 'react';

import type { Amount as AmountType } from '@shared/core-types';

import {
    buildLocalizedAmountProps,
    computeColorProp,
    ensureAmountObject,
} from './AmountLocalizationService';

interface LocalizedAmountProps {
    amount?: Partial<AmountType> | number | null;
    sizeLevel?: HeadingProps['level'];
    coloured?: boolean;
    showNegativeInRedColor?: boolean;
    'data-cy'?: string;
}

type WrapperProps<C extends ElementType> = Omit<
    ComponentPropsWithRef<C> & LocalizedAmountProps,
    'value' | 'precision' | 'currency'
>;

type LocalizedComponent<C extends ElementType> = FC<WrapperProps<C>> &
    AmountStatics & { wrapsComponent: C };

export function localizedAmountComponentService<C extends ElementType>(
    WrappedComponent: C,
    isColouredByDefault = false,
): LocalizedComponent<C> {
    type Props = WrapperProps<C>;

    const WrappedComponentWithSize = ({ sizeLevel, ...rest }: Props) => {
        if (sizeLevel) {
            return (
                <Heading level={sizeLevel} fake first>
                    {/* eslint-disable-next-line react/jsx-props-no-spreading */}
                    <WrappedComponent {...(rest as any)} />
                </Heading>
            );
        }

        // eslint-disable-next-line react/jsx-props-no-spreading
        return <WrappedComponent {...(rest as any)} />;
    };

    const LocalizedAmount: WrapperFC<Omit<Props, 'value'>> & AmountStatics = ({
        amount,
        coloured = isColouredByDefault,
        showNegativeInRedColor,
        fallbackText,
        useDemoAmount: useDemoAmountProp = false,
        ...passThroughProps
    }) => {
        if ((amount === undefined || amount === null) && !fallbackText) {
            return null;
        }

        if ((amount === undefined || amount === null) && fallbackText) {
            return WrappedComponentWithSize({
                ...(passThroughProps as any),
                fallbackText,
            });
        }

        const amountObject = ensureAmountObject(amount);
        const { ...localizedAmountProps } = buildLocalizedAmountProps(amountObject);
        const color = computeColorProp(
            amountObject,
            passThroughProps.color,
            coloured,
            showNegativeInRedColor,
        );

        return WrappedComponentWithSize({
            ...amountObject,
            ...localizedAmountProps,
            ...(passThroughProps as any),
            useDemoAmount: useDemoAmountProp,
            color,
        });
    };

    LocalizedAmount.wrapsComponent = WrappedComponent;
    LocalizedAmount.COLOR = Amount.COLOR;
    LocalizedAmount.ICON = Amount.ICON;
    LocalizedAmount.SUPERSCRIPT = Amount.SUPERSCRIPT;

    return LocalizedAmount as unknown as LocalizedComponent<C>;
}
